import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { learnPath, translatePath, wordsPath } from "../../utils/routes";
import { AnimatePresence, motion } from "framer-motion";
import { CheckIcon, ChevronDownIcon, PlusIcon } from "lucide-react";
import { useLearner } from "../../contexts/LearnerContext";
import { PairChips } from "../ui/LangChip";
import { courseTitle, getLanguage } from "../../utils/language";
import { spring } from "../../utils/motion";
interface CourseSwitcherProps {
  compact?: boolean;
}
export function CourseSwitcher({
  compact = false
}: CourseSwitcherProps) {
  const {
    courses,
    activeCourse,
    setActiveCourseId
  } = useLearner();
  const {
    pathname
  } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);
  if (!activeCourse) {
    return <Link to="/app/courses" className="focus-ring flex items-center gap-2 rounded-xl border-3 border-dashed border-line px-3 py-2 text-sm font-semibold text-ink" data-id="element-491" data-mp="rnn871ivn21w">
        <PlusIcon className="h-4 w-4" data-id="element-492" data-mp="itico4sst2g1" /> Add a course
      </Link>;
  }
  return <div ref={ref} className="relative" data-id="element-493" data-mp="dvqkras5lgbu">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-haspopup="listbox" className={`focus-ring flex w-full items-center gap-3 rounded-xl border-3 border-line bg-surface text-left ${compact ? "px-2 py-1.5" : "px-3 py-2.5 shadow-brutal-sm"}`} data-id="element-494" data-mp="dwrw7gd304u3">
        <PairChips target={activeCourse.target} base={activeCourse.base} size="sm" data-id="element-495" data-mp="3vdmdl4limkd4" />
        {!compact && <span className="min-w-0 flex-1 pl-1" data-id="element-496" data-mp="3hempby1x07hh">
            <span className="block truncate text-sm font-bold text-ink" data-id="element-497" data-mp="3hfxt3xvd1f8l">{getLanguage(activeCourse.target).name}</span>
            <span className="block truncate text-xs text-ink-2" data-id="element-498" data-mp="3lhjde4a84vst">from {getLanguage(activeCourse.base).name}</span>
          </span>}
        <motion.span animate={{
        rotate: open ? 180 : 0
      }} transition={spring.snappy} data-id="element-499" data-mp="hz996jt15n12">
          <ChevronDownIcon className="h-4 w-4 text-ink-2" strokeWidth={2.5} data-id="element-500" data-mp="47uswkqibzxp" />
        </motion.span>
      </button>

      <AnimatePresence data-id="element-501" data-mp="88enbgjp4pg5">
        {open && <motion.div role="listbox" aria-label="Your courses" initial={{
        opacity: 0,
        y: -6,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.98,
        transition: {
          duration: 0.12
        }
      }} transition={spring.stable} className={`absolute z-50 mt-2 origin-top rounded-card border-3 border-line bg-surface p-2 shadow-brutal ${compact ? "left-0 w-72" : "left-0 right-0"}`} data-id="element-502" data-mp="873ireiffdnz">
            {courses.map(c => {
          const active = c.id === activeCourse.id;
          return <button key={c.id} type="button" role="option" aria-selected={active} onClick={() => {
            setActiveCourseId(c.id);
            setOpen(false);
            // Stay in the same section, but for the newly picked pair.
            if (pathname.startsWith("/app/learn")) navigate(learnPath(c));else if (pathname.startsWith("/app/words")) navigate(wordsPath(c));else if (pathname.startsWith("/app/translate")) navigate(translatePath(c.base, c.target));
          }} className={`focus-ring flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-left transition-colors duration-150 ${active ? "bg-brand-tint" : "hover:bg-sunken"}`} data-id="element-503" data-mp="22gigw6tlkoki">
                  <PairChips target={c.target} base={c.base} size="sm" data-id="element-504" data-mp="26e7p826lnmt6" />
                  <span className="min-w-0 flex-1 pl-1" data-id="element-505" data-mp="2bd244t1g4r80">
                    <span className="block truncate text-sm font-semibold text-ink" data-id="element-506" data-mp="2bciyobszfvav">{courseTitle(c)}</span>
                    <span className="block text-xs text-ink-2" data-id="element-507" data-mp="2gaaq74pptfej">Level {c.level}</span>
                  </span>
                  {active && <CheckIcon className="h-4 w-4 text-ink" strokeWidth={3} data-id="element-508" data-mp="2l57ww4zivco9" />}
                </button>;
        })}
            <Link to="/app/courses" onClick={() => setOpen(false)} className="focus-ring mt-1 flex items-center gap-2 rounded-xl border-t-2 border-dashed border-line/30 px-2.5 py-2.5 text-sm font-semibold text-ink hover:bg-sunken" data-id="element-509" data-mp="2u420ifb9t3bb">
              <PlusIcon className="h-4 w-4" strokeWidth={2.5} data-id="element-510" data-mp="347qjlujf2gn9" /> Add a course
            </Link>
          </motion.div>}
      </AnimatePresence>
    </div>;
}